import axios from "axios";
import {
  applyCsrfHeader,
  CSRF_FAILURE_STATUS,
  withFriendlyCsrfMessage,
} from "../utils/csrf";
import { formatApiErrorMessage } from "../utils/formatApiErrorMessage";

export { formatApiErrorMessage };

// const BASE_URL = process.env.REACT_APP_API_BASE_URL;
// Axios instance with base URL from .env
export const API = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL, // Use the base URL from .env
  withCredentials: true, // Required for browser to accept and persist cross-origin HttpOnly cookies
  headers: {
    Accept: "application/json",
  },
});

// ─── Token Refresh Mutex ────────────────────────────────────────────────────
// Prevents multiple concurrent 401s from all triggering redirects.
// When one request refreshes the session, all others queue and retry silently.
let _isRefreshing = false;
let _refreshSubscribers = [];

function onRefreshed(newToken) {
  _refreshSubscribers.forEach((cb) => cb(newToken));
  _refreshSubscribers = [];
}

function subscribeToRefresh(cb) {
  _refreshSubscribers.push(cb);
}
// ────────────────────────────────────────────────────────────────────────────

// console.log("API", API);
// Add interceptors for Authorization

API.interceptors.request.use(
  (config) => {
    // We intentionally do NOT set the Authorization header here anymore.
    // The backend uses the HttpOnly pms_session cookie for authentication and
    // requires the XSRF-TOKEN cookie echoed back as a header on mutating requests.
    // Axios only does that on its own for same-origin URLs, so set it explicitly.
    applyCsrfHeader(config.headers);
//----------------------------------------------------------
    // updated project name immediate shows:
        // Disable caching for GET requests
    if (config.method === 'get') {
      config.headers['Cache-Control'] = 'no-cache, no-store, must-revalidate';
      config.headers['Pragma'] = 'no-cache';
      config.headers['Expires'] = '0';
      
      // Add a timestamp query param to force a unique URL (bypasses Cloudflare/Nginx caches)
      config.params = {
        ...config.params,
        _t: new Date().getTime(),
      };
    }
//----------------------------------------------------------
    return config;
  },
  (error) => Promise.reject(error)
);

API.interceptors.response.use(
  (response) => {
    // Read auto-refreshed JWT from JWTAuthenticationMiddleware
    const newAuthHeader = response.headers['authorization'];
    if (newAuthHeader && newAuthHeader.startsWith('Bearer ')) {
      const newToken = newAuthHeader.replace('Bearer ', '');
      // Notify any queued requests waiting for a fresh token
      // We no longer store it in localStorage due to XSS concerns.
      if (_isRefreshing) {
        _isRefreshing = false;
        onRefreshed(newToken);
      }
    }
    return response;
  },
  (error) => {
    const originalRequest = error.config;

    // 419: the CSRF cookie/header pair is missing or does not match. A refresh
    // cannot fix that, so just give the user a message they can act on.
    if (error.response && error.response.status === CSRF_FAILURE_STATUS) {
      error.response.data = withFriendlyCsrfMessage(error.response.data);
      return Promise.reject(error);
    }

    // Check if the error status is 401 (Unauthorized)
    if (error.response && error.response.status === 401) {
      const requestUrl = originalRequest?.url || "";
      const isAuthEndpoint =
        requestUrl.includes("sso/session-user") ||
        requestUrl.includes("auth/google") ||
        requestUrl.includes("login");

      // Silent-check endpoints: never redirect, never wipe token
      if (isAuthEndpoint) {
        return Promise.reject(error);
      }

      // If already refreshing (another request triggered the refresh),
      // queue this request to retry once the fresh token is available
      if (_isRefreshing) {
        return new Promise((resolve) => {
          subscribeToRefresh((newToken) => {
            originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
            resolve(API(originalRequest));
          });
        });
      }

      // Mark refresh in progress
      _isRefreshing = true;

      // Try to restore session silently via pms_session cookie
      return fetch(`${process.env.REACT_APP_API_BASE_URL}sso/session-user`, {
        headers: { Accept: "application/json" },
        credentials: "include",
      })
        .then((res) => {
          if (res.ok) {
            return res.json().then((data) => {
              if (data && data.status && (data.token || data.access_token)) {
                const freshToken = data.token || data.access_token;

                localStorage.setItem("access_token", "secure-cookie-auth"); // Store dummy token for frontend auth state

                _isRefreshing = false;
                onRefreshed(freshToken);

                // Retry the original failed request
                // The browser will automatically attach the new HttpOnly pms_session cookie
                return API(originalRequest);
              } else {
                // Session genuinely ended — go to login
                _isRefreshing = false;
                _refreshSubscribers = [];
                localStorage.clear();
                sessionStorage.clear();
                if (window.location.pathname !== "/login") {
                  window.location.href = "/login";
                }
                return Promise.reject(error);
              }
            });
          } else {
            // Session genuinely ended
            _isRefreshing = false;
            _refreshSubscribers = [];
            localStorage.clear();
            sessionStorage.clear();
            if (window.location.pathname !== "/login") {
              window.location.href = "/login";
            }
            return Promise.reject(error);
          }
        })
        .catch(() => {
          _isRefreshing = false;
          _refreshSubscribers = [];
          return Promise.reject(error);
        });
    }

    if (error.response && error.response.status === 302) {
      window.location.href = "/twofactor-scan";
    }
    if (error.response && error.response.status === 307) {
      window.location.href = "/twofactor-auth";
    }
    return Promise.reject(error);
  }
);

/**
 * API Methods
 */

// Login User
export const loginUser = async (email, password) => {
  try {
    const response = await API.post("login", { email, password });
    return response.data;
  } 
  catch (error) {
    throw error.response ? error.response.data : error;
  }
};

// Google SSO Login User
export const googleLoginUser = async (credential) => {
  try {
    const response = await API.post("auth/google", { credential });
    return response.data;
  } catch (error) {
    throw error.response ? error.response.data : error;
  }
};
export const editCommentEntities = async (endpoint, id, requestData) => {
  try {
    const response = await API.put(`${endpoint}/${id}`, requestData);
    return response.data; // Return the response data
  } 
  catch (error) {
    console.error(
      `Error editing ${endpoint} with ID ${id}:`,
      error.response || error
    );
    throw error.response ? error.response.data : error;
  }
};

// Forgot Password
export const forgotPassword = async (email) => {
  try {
    const response = await API.post("forgot-password", { email });
    return response.data;
  } 
  catch (error) {
    throw error.response ? error.response.data : error;
  }
};

// Reset Password
export const resetPassword = async (token, password, password_confirmation) => {
  try {
    const response = await API.post(`reset-password/${token}`, {
      password,
      password_confirmation,
    });
    return response.data;
  } 
  catch (error) {
    throw error.response ? error.response.data : error;
  }
};

export const createPassword = async (
  token,
  password,
  password_confirmation
) => {
  try {
    const response = await API.post(`create-password/${token}`, {
      password,
      password_confirmation,
    });
    return response.data;
  } 
  catch (error) {
    throw error.response ? error.response.data : error;
  }
};

// export const resetPassword = async (
//   token,
//   password,
//   password_confirmation
// ) => {
//   try {
//     const response = await API.post('reset-password', {
//       token,
//       password,
//       password_confirmation,
//     });
//     return response.data;
//   } catch (error) {
//     throw error.response ? error.response.data : error;
//   }
// };

// Logout User
export const logoutUser = async () => {
  try {
    const response = await API.post("sso/logout");
    localStorage.removeItem("access_token"); // Clear token on logout
    return response.data;
  } 
  catch (error) {
    throw error.response ? error.response.data : error;
  }
};

// Add Project
export const addProject = async (projectData) => {
  try {
    const response = await API.post("add-projects", projectData);
    return response.data;
  } 
  catch (error) {
    throw error.response ? error.response.data : error;
  }
};

export const getAllData = async (endpoint, projectData) => {
  try {
    const response = await API.post(endpoint, projectData);
    return response.data;
  } catch (error) {
    throw error.response ? error.response.data : error;
  }
};

// Fetch All Projects
export const fetchAllProjects = async () => {
  try {
    const response = await API.get("add-projects");
    return response.data;
  } 
  catch (error) {
    throw error.response ? error.response.data : error;
  }
};

// Filter Projects
export const filterProjects = async (filterCriteria) => {
  try {
    const response = await API.get("projects/filter", {
      params: filterCriteria,
    });
    return response.data;
  } 
  catch (error) {
    throw error.response ? error.response.data : error;
  }
};

/**
 * Generic Service Functions
 */
// export const createEntity = async (endpoint, data) => {
//   try {
//     const response = await API.post(endpoint, data);
//     return response.data;
//   } catch (error) {
//     throw error.response ? error.response.data : error;
//   }
// };


export const createEntity = async (endpoint, data, token) => {
  try {
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const response = await API.post(endpoint, data, { headers });
    return response.data;
  } catch (error) {
    if (error.response) {
      const errData = error.response.data;
      if (typeof errData === "object" && errData !== null) {
        errData.status_code = error.response.status;
        errData.status = errData.status ?? error.response.status;
        throw errData;
      }
      const err = new Error(formatApiErrorMessage(error));
      err.status = error.response.status;
      err.response = error.response;
      throw err;
    }
    throw error;
  }
};

export const fetchAllEntities = async (endpoint) => {
  try {
    const response = await API.get(endpoint);
    return response.data;
  } 
  catch (error) {
    throw error.response ? error.response.data : error;
  }
};

export const deteleteImage = async (endpoint) => {
  try {
    const response = await API.delete(endpoint);
    return response.data; // Return the response data
  } 
  catch (error) {
    // Log and rethrow the error
    console.error(`Error deleting ${endpoint}:`, error.response || error);
    throw error.response ? error.response.data : error;
  }
};

export const deleteParams = async (endpoint, id, params) => {
  try {
    const response = await API.delete(`${endpoint}/${id}`, { params });
    return response.data; // Return the response data
  } 
  catch (error) {
    // Log and rethrow the error
    console.error(
      `Error deleting ${endpoint} with ID ${id}:`,
      error.response || error
    );
    throw error.response ? error.response.data : error;
  }
};

// delete comment entity
export const deleteCommentEntities = async (endpoint, id) => {
  try {
    const response = await API.delete(`${endpoint}/${id}`);
    return response.data; // Return the response data
  }
   catch (error) {
    // Log and rethrow the error
    console.error(
      `Error deleting ${endpoint} with ID ${id}:`,
      error.response || error
    );
    throw error.response ? error.response.data : error;
  }
};

export const fetchEntityById = async (endpoint, id) => {
  try {
    // Dynamically construct the URL with the endpoint and ID
    const response = await API.get(`${endpoint}/${id}`);
    return response.data; // Return the response data
  }
   catch (error) {
    // Log and rethrow the error
    console.error(
      `Error fetching ${endpoint} with ID ${id}:`,
      error.response || error
    );
    throw error.response ? error.response.data : error;
  }
};

export const editProjectDetails = async (endpoint, projectId, projectData) => {
  try {
    const response = await API.post(`${endpoint}/${projectId}`, projectData);
    // Return the response data
    return response.data;
  }
   catch (error) {
    // Log and rethrow the error
    console.error(
      `Error editing project ${projectId} at ${endpoint}:`,
      error.response || error
    );
    throw error.response ? error.response.data : error;
  }
};

// export const filterEntities = async (endpoint, filterCriteria) => {
//   try {
//     const response = await API.get(endpoint, { params: filterCriteria });
//     return response.data;
//   } catch (error) {
//     throw error.response ? error.response.data : error;
//   }
// };

//  export const newFilterEntities = async (endpoint, responseData) => {
//   try {
//     const response = await API.get(endpoint, responseData);
//     // response.data
//     //   ? response.data
//     //   : // console.log("unexpected API response in new filter entities");

//     return response.data;
//   } catch (error) {
//     console.error("Error in newFilterEntities:", error);
//   }
// };

export const newFilterEntities = async (endpoint, filterId, queryParams) => {
  try {
    const response = await API.get(`${endpoint}/${filterId}`, {
      params: queryParams,
    });
    return response.data;
  } 
  catch (error) {
    console.error("Error in newFilterEntities:", error);
    // throw {
    //   status: false,
    //   message: error.response?.data?.message || "An unexpected error occurred.",
    // };
      const customError = new Error(
      error.response?.data?.message || "An unexpected error occurred."
    );
    customError.status = false;
    throw customError;
  }
};

export const filterEntities = async (endpoint, filterCriteria = {}) => {
  try {
    const response = await API.get(endpoint, { params: filterCriteria });
    // const token = localStorage.getItem("access_token");
    // console.log("Token???", token);
    // Ensure the response is in the expected format
    if (response?.data) {
      return response.data;
    } else {
      throw new Error("Unexpected API response format.");
    }
  } catch (error) {
    console.error("Error in filterEntities:", error);

    // Handle cases where the error response is unavailable
    throw (
      error.response?.data || {
        status: false,
        message: "An unexpected error occurred. Please try again later.",
      }
    );
  }
};
// export const filterEntitiesscope = async (endpoint, filter) => {
//   try {
//     // console.log("Endpoint:", endpoint);
//     // console.log("Filter:", filter);
//     const url = `${endpoint}/${filter}`;
//     const response = await API.get(url);
//     if (response?.data) {
//       return response.data;
//     } else {
//       throw new Error("Unexpected API response format.");
//     }
//   } catch (error) {
//     console.error("Error in filterEntities:", error);
//     throw (
//       error.response?.data || {
//         status: false,
//         message: "An unexpected error occurred. Please try again later.",
//       }
//     );
//   }
// };

//  Resuable Api services

// 1. Add Comment
export const addComment = async (resourceType, resourceId, commentText, attachmentFile) => {
  try {
    const formData = new FormData();
    formData.append("comments", commentText);
    if (attachmentFile) {
      formData.append("attachment", attachmentFile);
    }
    const response = await API.post(
      `${resourceType}/comments/add/${resourceId}`,
      formData,
      {
        // const response = await API.post(`projects/comments/add/${projectId}`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response.data;
  } 
  catch (error) {
    console.error("Error adding comment:", error);
    throw error.response ? error.response.data : error;
  }
};

// 2. Edit Comment
export const editComment = async (resourceType, commentId, commentText, attachmentFile) => {
  try {
    const formData = new FormData();
    formData.append("comments", commentText);
    if (attachmentFile) {
      formData.append("attachment", attachmentFile);
    }
    const response = await API.post(
      `${resourceType}/comments/${commentId}`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response.data;
  } 
  catch (error) {
    console.error("Error editing comment:", error);
    throw error.response ? error.response.data : error;
  }
};

// 3. Delete Comment
export const deleteComment = async (resourceType, commentId) => {
  try {
    const response = await API.delete(`${resourceType}/comments/${commentId}`);
    return response.data;
  } 
  catch (error) {
    console.error("Error deleting comment:", error);
    throw error.response ? error.response.data : error;
  }
};

// 4. Show All Comments
export const getAllComments = async (resourceType, resourceId) => {
  try {
    const response = await API.get(`${resourceType}/comments/${resourceId}`);
    return response.data;
  } 
  catch (error) {
    console.error("Error fetching all comments:", error);
    throw error.response ? error.response.data : error;
  }
};

export const addData = async (endpoint, projectId, data) => {
  try {
    const response = await API.post(`${endpoint}/add/${projectId}`, data);
    return response.data;
  } 
  catch (error) {
    throw error.response ? error.response.data : error;
  }
};

// export const addSubTask = async (resourceType, actionType, resourceId, subtaskData) => {
//   try {
//     // Dynamically create the URL based on resourceType and actionType
//     const url = `${resourceType}/${actionType}/${resourceId}`;

//     // Make the POST request using the dynamically built URL and subtask data
//     const response = await API.post(url, subtaskData);

//     // Return the response data
//     return response.data;
//   } catch (error) {
//     console.error('Error adding subtask:', error);
//     throw error.response ? error.response.data : error; // Rethrow the error after logging
//   }
// };

export const postResource = async ( basePath, actionType, resourceId, postData) => {
  try {
    // Dynamically create the URL based on basePath, actionType, and resourceId
    const url = `${basePath}/${actionType}/${resourceId}`;

    // Make the POST request with the provided data
    const response = await API.post(url, postData);

    // Return the response data
    return response.data;
  } 
  catch (error) {
    console.error(`Error posting resource to `, error); // Ensure url is used after it's defined
    throw error.response ? error.response.data : error; // Rethrow the error after logging
  }
};

export const postdatas = async (basePath, actionType, postData) => {
  try {
    // Dynamically create the URL based on basePath, actionType, and resourceId
    const url = `${basePath}/${actionType}`;

    // Make the POST request with the provided data
    const response = await API.post(url, postData);

    // Return the response data
    return response.data;
  } 
  catch (error) {
    console.error(`Error posting resource to `, error); // Ensure url is used after it's defined
    throw error.response ? error.response.data : error; // Rethrow the error after logging
  }
};

export const postNotification = async (basePath, id) => {
  try {
    const response = await API.post(`${basePath}/${id}`);
    return response.data;
  } 
  catch (error) {
    throw error.response ? error.response.data : error;
  }
};

export const fetchResource = async (basePath, actionType, resourceId) => {
  try {
    // Dynamically create the URL based on basePath, actionType, and resourceId
    const url = `${basePath}/${actionType}/${resourceId}`;

    // Make the GET request
    const response = await API.get(url);

    // Return the response data
    return response.data;
  } 
  catch (error) {
    console.error(`Error fetching resource from `, error);
    throw error.response ? error.response.data : error; // Rethrow the error after logging
  }
};

export const getAllNameList = async (basePath) => {
  try {
    const response = await API.get(basePath);
    return response.data;
  } 
  catch (error) {
    console.error(`Error fetching all project names from `, error);
    throw error.response ? error.response.data : error; // Rethrow the error after logging
  }
};

export const addDocument = async (resourceType, resourceId, documentFiles) => {
  try {
    const formData = new FormData();

    // Append the document files
    if (documentFiles) {
      documentFiles.forEach((file) => {
        formData.append("document[]", file);
      });
    }

    // Dynamically construct the URL
    const apiUrl = `${resourceType}/document/add/${resourceId}`;

    // Make the POST request
    const response = await API.post(apiUrl, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    return response.data; // Return the response
  } 
  catch (error) {
    console.error(`Error adding document to task`, error);
    throw error.response ? error.response.data : error; // Rethrow for higher-level error handling
  }
};

//fetch weekly task allocation
export const getWeeklyTaskAllocation = async (basePath) => {
  try {
    const response = await API.get(basePath);
    return response.data;
  } 
  catch (error) {
    console.error(`Error fetching all project  `, error);
    throw error.response ? error.response.data : error; // Rethrow the error after logging
  }
};

// update status of weekly task allocation
export const updateWeeklyTaskAllocation = async (basePath, payload, token) => {
  try {
    const response = await API.post(basePath, payload, {
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${token}`, // 🔹 add Bearer token
      },
    });
    return response.data;
  } 
  catch (error) {
    console.error(`Error updating project status`, error);
    throw error.response ? error.response.data : error;
  }
};

export const fetch2factor = async (basePath, token) => {
  try {
    const response = await API.get(basePath, {
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error(`Error fetching 2factor:`, error);
    throw error.response ? error.response.data : error;
  }
};

export const update2factor = async (basePath, payload, token) => {
  try {
    const response = await API.post(basePath, payload, {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error(`Error updating 2factor:`, error);
    throw error.response ? error.response.data : error;
  }
};

//fetch all my leave request 
export const getMyLeaveRequest = async (basePath) => {
  try {
    const response = await API.get(basePath);
    return response.data;
  } catch (error) {
    console.error(`Error fetching leave request  `, error);
    throw error.response ? error.response.data : error; // Rethrow the error after logging
  }
};

// Apply Leave Request
export const ApplyLeaveRequest = async (basePath, payload, token) => {
  try {
    const response = await API.post(basePath, payload, {
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${token}`, // 🔹 add Bearer token
      },
    });
    return response.data;
  } 
  catch (error) {
    console.error(`Error Apply Leave Request`, error);
    throw error.response ? error.response.data : error;
  }
};

export const fetch2factorUsers = async (basePath, token) => {
  try {
    const response = await API.get(basePath, {
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error(`Error fetching users:`, error);
    throw error.response ? error.response.data : error;
  }
};

// Specific delete function for appraisal feedback using correct endpoint
export const deleteAppraisalFeedback = async (id) => {
  try {
    const response = await API.delete(`/appraisalFeedback/deleteAppraisalFeedback/${id}`);
    return response.data;
  } 
  catch (error) {
    throw error.response ? error.response.data : error;
  }
};

// Delete Task
export const deleteTask = async (id) => {
  try {
    const response = await API.delete(`task/delete/${id}`);
    return response.data;
  } 
  catch (error) {
    console.error(`Error deleting task ${id}:`, error);
    throw error.response ? error.response.data : error;
  }
};

export const deleteSubtask = async (taskId) => {
  try {
    const response = await API.delete(`task/delete/${taskId}`);
    return response.data;
  } 
  catch (error) {
    throw error;
  }
};

export const updateSubtask = async (taskId, postData) => {
  try {
    const response = await API.post(`task/update/${taskId}`, postData);
    return response.data;
  } catch (error) {
    throw error;
  }
};





// export default {
const apiService = {
  loginUser,
  googleLoginUser,
  forgotPassword,
  resetPassword,
  logoutUser,
  addProject,
  fetchAllProjects,
  filterProjects,
  fetchAllEntities,
  addComment,
  editComment,
  postdatas,
  deleteComment,
  getAllComments,
  addData,
  getAllData,
  postResource,
  addDocument,
  fetchEntityById,
  deleteCommentEntities,
  postNotification,
  // addSubTask
  getWeeklyTaskAllocation,
  updateWeeklyTaskAllocation,
  fetch2factor,
  update2factor,
  getMyLeaveRequest,
  ApplyLeaveRequest,
  fetch2factorUsers,

  filterEntities,
  deleteAppraisalFeedback,
  deleteTask,

// subtask delete 
  deleteSubtask,
  updateSubtask
};

export default apiService;


